import { Link } from 'react-router-dom';
import type {
  DashboardActivityItem,
  DashboardFunnelStage,
  DashboardMetric,
} from '../../../shared/models';
import {
  HabitaSectionHeader,
  PastaStatusBadge,
  classNames,
  habitaSurfaceClass,
} from '../../../shared/components/habita';
import {
  createPastaDetailNavigationState,
  rememberPastaDetailExternalIds,
} from '../../../shared/navigation/pastaDetailNavigation';
import { formatHabitaShortDateTime } from '../../../shared/utils';

const metricToneClasses: Record<DashboardMetric['tone'], string> = {
  brand: 'text-primary',
  success: 'text-emerald-700',
  warning: 'text-amber-700',
  info: 'text-sky-700',
  neutral: 'text-textPrimary',
};

function processIdFromActivityHref(href: string): string | undefined {
  const processId = href.split('/').filter(Boolean).pop();
  return processId ? decodeURIComponent(processId) : undefined;
}

function rememberActivityExternalIds(activity: DashboardActivityItem): void {
  rememberPastaDetailExternalIds(processIdFromActivityHref(activity.href), {
    chatwootContactId: activity.chatwootContactId,
    crmContactId: activity.crmContactId,
  });
}

export function DashboardMetricCard({ metric }: { metric: DashboardMetric }) {
  return (
    <article className={classNames(habitaSurfaceClass, 'border-l-4 border-l-primary/60 p-4 sm:p-5')}>
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="font-instrument text-xs font-semibold uppercase tracking-wide text-textSecondary">
            {metric.label}
          </p>
          <p className="mt-2 font-instrument text-2xl font-semibold text-textPrimary">{metric.value}</p>
          <p className="mt-1 line-clamp-2 font-instrument text-xs text-textSecondary">
            {metric.description}
          </p>
        </div>
        <i className={`${metric.icon} shrink-0 text-lg ${metricToneClasses[metric.tone]}`} aria-hidden />
      </div>
    </article>
  );
}

export function DashboardFunnel({
  stages,
}: {
  stages: DashboardFunnelStage[];
}) {
  return (
    <section className={classNames(habitaSurfaceClass, 'h-full')}>
      <HabitaSectionHeader title="Funil operacional" className="border-b-0 pb-3 pt-5" titleClassName="text-base" />
      <div className="space-y-4 px-5 pb-5 pt-0">
        {stages.map((stage) => (
          <div key={stage.status} className="space-y-2">
            <div className="flex items-center justify-between gap-4">
              <div className="min-w-0">
                <p className="font-instrument text-sm font-medium text-textPrimary">{stage.label}</p>
              </div>
              <span className="shrink-0 font-instrument text-sm text-textSecondary">
                {stage.count} pastas · {stage.percentage}%
              </span>
            </div>
            <div className="h-2.5 overflow-hidden rounded-full bg-muted" title={stage.description}>
              <div
                className="h-full rounded-full bg-primary transition-all"
                style={{ width: `${stage.percentage}%` }}
              />
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

export function DashboardActivities({ activities }: { activities: DashboardActivityItem[] }) {
  return (
    <section className={classNames(habitaSurfaceClass, 'h-full')}>
      <HabitaSectionHeader title="Últimas atualizações" className="border-b-0 pb-3 pt-5" titleClassName="text-base" />
      <div className="space-y-1 px-3 pb-3 pt-0">
        {activities.length === 0 ? (
          <p className="p-5 font-instrument text-sm text-textSecondary">Nenhuma atualização recente.</p>
        ) : (
          activities.map((activity) => (
            <Link
              key={activity.id}
              to={activity.href}
              state={createPastaDetailNavigationState('dashboard', activity)}
              onClick={() => rememberActivityExternalIds(activity)}
              className="flex items-center justify-between gap-3 rounded-md p-2 transition-colors hover:bg-accent"
            >
              <div className="min-w-0">
                <p className="truncate font-instrument text-sm font-medium text-textPrimary">
                  {activity.clienteNome}
                </p>
                <p className="truncate font-instrument text-xs text-textSecondary">
                  {activity.cpf} · {activity.responsavel}
                </p>
                <p className="mt-0.5 truncate font-instrument text-xs text-textSecondary/80">
                  {activity.message} · {formatHabitaShortDateTime(activity.updatedAt)}
                </p>
              </div>
              <PastaStatusBadge status={activity.status} />
            </Link>
          ))
        )}
      </div>
    </section>
  );
}

export function DashboardLoadingState() {
  return (
    <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
      {Array.from({ length: 8 }).map((_, index) => (
        <div key={index} className="h-32 animate-pulse rounded-lg border border-l-4 border-border-subtle border-l-primary/30 bg-card p-5">
          <div className="h-3 w-28 rounded bg-muted" />
          <div className="mt-5 h-8 w-16 rounded bg-muted" />
          <div className="mt-5 h-3 w-full rounded bg-muted/70" />
        </div>
      ))}
    </div>
  );
}
